import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./Navbar.css";

const departments = [
  { name: "Gram Panchayat Vibhag", path: "/department/gram-panchayat" },
  { name: "Secondary Education Vibhag", path: "/department/secondary-education" },
  { name: "District Rural Development Agency", path: "/department/drda" },
  { name: "Finance Vibhag", path: "/department/finance" },
  { name: "Social Welfare Vibhag", path: "/department/social-welfare" },
  { name: "Mahila Bal Kalyan Vibhag", path: "/department/mahila-bal-kalyan" },
  { name: "Pashusamvardhan Vibhag", path: "/department/pashusamvardhan" },
  { name: "Krushi Vibhag", path: "/department/krushi" },
  { name: "Gramin Pani Puravatha Vibhag", path: "/department/gramin-pani-puravatha" },
  { name: "Arogya Vibhag", path: "/department/arogya" },
  { name: "Jilha Pani Swachhata Mission", path: "/department/jilha-pani-swachhata" },
  { name: "Laghu Paatbandhare Vibhag", path: "/department/laghu-paatbandhare" },
];

const MOBILE_BREAKPOINT = 768;

const Navbar = () => {
  const [fontSize, setFontSize] = useState(16);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const dropdownRef = useRef(null);
  const navRef = useRef(null);
  const hamburgerRef = useRef(null);

  const isMobile = () =>
    typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT;

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}px`;
  }, [fontSize]);

  useEffect(() => {
    document.cookie = "googtrans=/mr/mr;path=/";

    const addTranslate = () => {
      if (!window.google || !window.google.translate) {
        const script = document.createElement("script");
        script.src =
          "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
        script.async = true;
        document.body.appendChild(script);
      }

      window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "mr",
            includedLanguages: "mr,en",
            autoDisplay: false,
          },
          "google_translate_element"
        );
      };
    };

    addTranslate();
  }, []);

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    const changeLanguage = () => {
      const select = document.querySelector(".goog-te-combo");
      if (select) {
        select.value = lang;
        select.dispatchEvent(new Event("change"));
      } else {
        setTimeout(changeLanguage, 300);
      }
    };
    changeLanguage();
  };

  // Compact the header once the page is scrolled (mobile)
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dept dropdown when clicking outside of it (desktop hover dropdown)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDeptOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close the whole mobile drawer when tapping outside of it
  useEffect(() => {
    const handleClickOutsideMenu = (event) => {
      if (!menuOpen) return;
      if (
        navRef.current &&
        !navRef.current.contains(event.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
        setDeptOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideMenu);
    return () => document.removeEventListener("mousedown", handleClickOutsideMenu);
  }, [menuOpen]);

  // Close drawer on Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setDeptOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Auto-close drawer if resized back to desktop width
  useEffect(() => {
    const handleResize = () => {
      if (!isMobile()) {
        setMenuOpen(false);
        setDeptOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = menuOpen && isMobile() ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const increaseFont = () => setFontSize((prev) => Math.min(prev + 2, 24));
  const decreaseFont = () => setFontSize((prev) => Math.max(prev - 2, 12));

  const closeAllMenus = () => {
    setMenuOpen(false);
    setDeptOpen(false);
  };

  const toggleMenu = () => {
    setMenuOpen((prev) => {
      const next = !prev;
      if (!next) setDeptOpen(false);
      return next;
    });
  };

  const toggleDeptDropdown = () => {
    if (isMobile()) setDeptOpen((prev) => !prev);
  };

  return (
    <header className={scrolled ? "scrolled" : ""}>
      {/* ==================== TOP BAR ==================== */}
      <div className="top-bar">
        <div className="top-container">
          <div className="top-left">
            <div className="logos">
              <img src="/images/embelm.png" alt="Indian Emblem" className="emblem" />
              <img src="/images/logobg.png" alt="Government Logo" className="govt-logo" />
            </div>
            <div className="govt-text">
              <span className="english">GOVERNMENT OF INDIA</span>
              <h2>NIMGAON KETKI</h2>
              <h3>Gram Panchayat</h3>
            </div>
          </div>

          <div className="top-right">
            <div className="accessibility-tools">
              <button className="font-btn" onClick={increaseFont} aria-label="Increase font size" type="button">A+</button>
              <button className="font-btn" onClick={decreaseFont} aria-label="Decrease font size" type="button">A-</button>
              <select className="language-select" onChange={handleLanguageChange} aria-label="Select language" defaultValue="en">
                <option value="en">English</option>
                <option value="mr">मराठी</option>
              </select>
            </div>
            <img src="/images/swachh.png" alt="Swachh Bharat" className="swachh-logo" />
          </div>
        </div>
      </div>

      {/* ==================== NAVBAR ==================== */}
      <nav className="main-nav" ref={navRef}>
        <div className="nav-container">
          <button
            ref={hamburgerRef}
            className={`hamburger ${menuOpen ? "open" : ""}`}
            onClick={toggleMenu}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="main-menu"
            type="button"
          >
            <span className="hamburger-icon">{menuOpen ? "✕" : "☰"}</span>
          </button>

          <ul id="main-menu" className={menuOpen ? "active" : ""}>
            <li className="drawer-header">
              <span>Menu</span>
              <button className="drawer-close" onClick={closeAllMenus} aria-label="Close menu" type="button">✕</button>
            </li>

            <li><Link to="/" onClick={closeAllMenus}>HOME</Link></li>
            <li><Link to="/about-us" onClick={closeAllMenus}>ABOUT</Link></li>
            <li><Link to="/department-schemes" onClick={closeAllMenus}>SCHEMES</Link></li>
            <li><Link to="/executive-board" onClick={closeAllMenus}>EXECUTIVE BOARD</Link></li>

            <li
              className="dept-dropdown-wrapper"
              ref={dropdownRef}
              onMouseEnter={() => { if (!isMobile()) setDeptOpen(true); }}
              onMouseLeave={() => { if (!isMobile()) setDeptOpen(false); }}
            >
              <button
                className="dept-dropdown-trigger"
                onClick={toggleDeptDropdown}
                aria-expanded={deptOpen}
                aria-haspopup="true"
                type="button"
              >
                <span>DEPARTMENTS</span>
                <span className={`dropdown-arrow ${deptOpen ? "rotate" : ""}`}>▼</span>
              </button>

              <div className={`dept-dropdown-menu ${deptOpen ? "open" : ""}`}>
                <div className="dept-dropdown-title">Departments</div>
                {departments.map((dept) => (
                  <Link key={dept.path} to={dept.path} className="dept-link" onClick={closeAllMenus}>
                    {dept.name}
                  </Link>
                ))}
                <div className="dept-dropdown-footer">{departments.length} Departments Available</div>
              </div>
            </li>

            <li><Link to="/contact" onClick={closeAllMenus}>CONTACT</Link></li>
          </ul>
        </div>
      </nav>

      {menuOpen && <div className="nav-overlay" onClick={closeAllMenus} aria-hidden="true"></div>}

      <div id="google_translate_element" style={{ position: "absolute", left: "-9999px", visibility: "hidden" }}></div>
    </header>
  );
};

export default Navbar;